import { createDialog } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { I18nService, type I18nService as I18nServiceType } from '@telecom/shared/services/i18n';
import { RavenService, type RavenService as RavenServiceType } from '@telecom/shared/services/raven-service/raven-service';
import {
    TRACK_EVENT_AGENT_FREE_FIRST_RUN_CLOSED,
    TRACK_EVENT_AGENT_FREE_FIRST_RUN_SHOWN,
} from '@/constants/track-events';
import { registerOnboardingLocaleMessages } from '../../utils/onboarding-locale';
import { messages } from '../../locales';
import {
    OnboardingBrowserRuntime,
    type OnboardingBrowserRuntime as OnboardingBrowserRuntimeType,
} from '../onboarding-browser-runtime';
import type {
    GuestFreeFirstRunPromptResult,
    GuestFreeFirstRunPromptService,
    GuestFreeFirstRunPromptShowOptions,
} from '../guest-free-first-run-prompt-service';

/** 页面进入后展示免费首跑提示前的等待时间。 */
const PROMPT_DELAY_MS = 1000;
/** 免费首跑提示自动关闭前的展示时长。 */
const PROMPT_DURATION_MS = 5000;
/** 弹窗离场完成后继续 Driver 引导前的缓冲时间。 */
const ONBOARDING_DELAY_AFTER_CLOSE_MS = 500;
/** 按需加载免费首跑提示组件的弹窗工厂。 */
const promptDialogFactory = createDialog(() => import('../../components/GuestFreeFirstRunPrompt.vue'));

/**
 * 游客免费首跑提示编排器。
 *
 * 负责动态语言注入、弹窗展示时序、关闭后的引导衔接和展示/关闭埋点。
 */
@injectable()
export class GuestFreeFirstRunPromptServiceImpl implements GuestFreeFirstRunPromptService {
    /** 当前 service 独占的延迟加载弹窗实例。 */
    private readonly _dialog = promptDialogFactory();
    /** 弹窗是否已经打开，用于销毁时决定是否主动关闭。 */
    private _isDialogOpen = false;
    /** 当前展示流程是否已因页面销毁而失效。 */
    private _isDestroyed = false;
    /** 关闭弹窗后等待继续 onboarding 的定时器。 */
    private _postCloseTimerId?: number;
    /** 销毁时用于提前结束关闭后等待的 Promise resolver。 */
    private _resolvePostCloseDelay?: () => void;

    constructor(
        /** 用于动态合并语言包和获取弹窗文案。 */
        @inject(I18nService)
        private readonly _i18nService: I18nServiceType,
        /** 用于上报免费首跑提示展示和关闭埋点。 */
        @inject(RavenService)
        private readonly _ravenService: RavenServiceType,
        /** 封装关闭后等待所需的浏览器定时器。 */
        @inject(OnboardingBrowserRuntime)
        private readonly _runtime: OnboardingBrowserRuntimeType,
    ) {}

    /**
     * 展示免费首跑提示，并在关闭动画及后续缓冲时间结束后返回。
     * @param options 当前 Agent 的埋点上下文。
     * @returns 弹窗结束原因；销毁或异常统一返回 destroyed。
     */
    async show(options: GuestFreeFirstRunPromptShowOptions): Promise<GuestFreeFirstRunPromptResult> {
        this._isDestroyed = false;
        registerOnboardingLocaleMessages(this._i18nService, messages);

        try {
            this._isDialogOpen = true;
            /** 用户关闭或自动关闭弹窗后的结果。 */
            const result = await this._dialog.open({
                title: this._i18nService.t('onboarding.freeFirstRun.title'),
                description: this._i18nService.t('onboarding.freeFirstRun.description'),
                closeLabel: this._i18nService.t('onboarding.freeFirstRun.close'),
                autoCloseLabel: this._i18nService.t('onboarding.freeFirstRun.autoClose'),
                delayMs: PROMPT_DELAY_MS,
                durationMs: PROMPT_DURATION_MS,
                onShown: () => this._trackShown(options),
            }) as GuestFreeFirstRunPromptResult;
            this._isDialogOpen = false;
            this._trackClosed(options, result.reason);
            await this._waitBeforeOnboarding();
            return this._isDestroyed ? { reason: 'destroyed' } : result;
        } catch {
            return { reason: 'destroyed' };
        } finally {
            this._isDialogOpen = false;
        }
    }

    /** 销毁当前弹窗并立即结束尚未完成的关闭后等待。 */
    destroy(): void {
        this._isDestroyed = true;
        if (this._isDialogOpen) {
            this._dialog.close();
        }
        this._cancelPostCloseDelay();
    }

    /** 等待弹窗离场后再把控制权交还给下一段 Driver 引导。 */
    private _waitBeforeOnboarding(): Promise<void> {
        return new Promise((resolve) => {
            this._resolvePostCloseDelay = resolve;
            this._postCloseTimerId = this._runtime.setTimeout(() => {
                this._postCloseTimerId = undefined;
                this._resolvePostCloseDelay = undefined;
                resolve();
            }, ONBOARDING_DELAY_AFTER_CLOSE_MS);
        });
    }

    /** 取消关闭后的等待，并确保对应 Promise 可以正常结束。 */
    private _cancelPostCloseDelay(): void {
        if (this._postCloseTimerId !== undefined) {
            this._runtime.clearTimeout(this._postCloseTimerId);
            this._postCloseTimerId = undefined;
        }
        /** 当前等待流程的 Promise resolver。 */
        const resolve = this._resolvePostCloseDelay;
        this._resolvePostCloseDelay = undefined;
        resolve?.();
    }

    /** 上报免费首跑提示已经实际展示。 */
    private _trackShown(options: GuestFreeFirstRunPromptShowOptions): void {
        void this._ravenService.track(TRACK_EVENT_AGENT_FREE_FIRST_RUN_SHOWN, {
            eventType: options.eventType,
        }).catch(() => undefined);
    }

    /**
     * 上报免费首跑提示关闭；页面销毁导致的关闭不计入用户行为。
     * @param options 当前 Agent 的埋点上下文。
     * @param closeReason 弹窗结束原因。
     */
    private _trackClosed(
        options: GuestFreeFirstRunPromptShowOptions,
        closeReason: GuestFreeFirstRunPromptResult['reason'],
    ): void {
        if (this._isDestroyed) return;
        void this._ravenService.track(TRACK_EVENT_AGENT_FREE_FIRST_RUN_CLOSED, {
            eventType: options.eventType,
            eventDetailStr: {
                closeReason,
            },
        }).catch(() => undefined);
    }
}
